import { HANDYOUT_SECTION_KEYS, type HandyoutSectionKey } from "@/types/handyout-modules";
import { MODULE_TYPE_META } from "@/lib/module-type-meta";
import { cn } from "@/lib/utils";
import type { AllModuleTypeNames } from "@/types/modules";
import type { ReactElement, ReactNode } from "react";

export type InsertableModuleType = Exclude<AllModuleTypeNames, HandyoutSectionKey>;

type Tone = "faint" | "soft" | "mid" | "strong" | "accent" | "accentSoft" | "paper";

const FILL: Record<Tone, string> = {
  faint: "fill-muted-foreground/10",
  soft: "fill-muted-foreground/20",
  mid: "fill-muted-foreground/40",
  strong: "fill-muted-foreground/55",
  accent: "fill-primary",
  accentSoft: "fill-primary/20",
  paper: "fill-background",
};

const STROKE: Record<Tone, string> = {
  faint: "stroke-muted-foreground/15",
  soft: "stroke-muted-foreground/30",
  mid: "stroke-muted-foreground/50",
  strong: "stroke-muted-foreground/65",
  accent: "stroke-primary",
  accentSoft: "stroke-primary/40",
  paper: "stroke-background",
};

function Box({
  x,
  y,
  w,
  h,
  r = 1.5,
  tone = "soft",
  stroke,
  dash,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  r?: number;
  tone?: Tone | "none";
  stroke?: Tone;
  dash?: string;
}) {
  return (
    <rect
      x={x}
      y={y}
      width={w}
      height={h}
      rx={r}
      className={cn(tone === "none" ? "fill-none" : FILL[tone], stroke && STROKE[stroke])}
      strokeWidth={stroke ? 1.25 : undefined}
      strokeDasharray={dash}
    />
  );
}

function Text({ x, y, w, tone = "soft" }: { x: number; y: number; w: number; tone?: Tone }) {
  return <Box x={x} y={y} w={w} h={4} r={2} tone={tone} />;
}

function Dot({
  x,
  y,
  r,
  tone = "mid",
  stroke,
}: {
  x: number;
  y: number;
  r: number;
  tone?: Tone | "none";
  stroke?: Tone;
}) {
  return (
    <circle
      cx={x}
      cy={y}
      r={r}
      className={cn(tone === "none" ? "fill-none" : FILL[tone], stroke && STROKE[stroke])}
      strokeWidth={stroke ? 1.5 : undefined}
    />
  );
}

function Stroke({
  d,
  tone = "soft",
  width = 1.5,
  dash,
}: {
  d: string;
  tone?: Tone;
  width?: number;
  dash?: string;
}) {
  return (
    <path
      d={d}
      className={cn("fill-none", STROKE[tone])}
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray={dash}
    />
  );
}

function Shape({ d, tone = "soft" }: { d: string; tone?: Tone }) {
  return <path d={d} className={FILL[tone]} />;
}

function Poly({ points, tone = "soft" }: { points: string; tone?: Tone }) {
  return <polygon points={points} className={FILL[tone]} />;
}

function Arc({
  x,
  y,
  r,
  start,
  length,
  width,
  tone,
  round = false,
}: {
  x: number;
  y: number;
  r: number;
  start: number;
  length: number;
  width: number;
  tone: Tone;
  round?: boolean;
}) {
  return (
    <circle
      cx={x}
      cy={y}
      r={r}
      pathLength={100}
      strokeDasharray={`${length} ${100 - length}`}
      strokeDashoffset={-start}
      strokeWidth={width}
      strokeLinecap={round ? "round" : "butt"}
      transform={`rotate(-90 ${x} ${y})`}
      className={cn("fill-none", STROKE[tone])}
    />
  );
}

function polarPoints(cx: number, cy: number, radii: readonly number[]): string {
  const step = (Math.PI * 2) / radii.length;
  return radii
    .map((r, i) => {
      const angle = -Math.PI / 2 + i * step;
      return `${(cx + r * Math.cos(angle)).toFixed(1)},${(cy + r * Math.sin(angle)).toFixed(1)}`;
    })
    .join(" ");
}

function polar(cx: number, cy: number, r: number, degrees: number): [number, number] {
  const angle = (degrees * Math.PI) / 180;
  return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
}

const HEAT_TONES: readonly Tone[] = ["faint", "soft", "mid", "strong"];

const MAP_DOTS: readonly [number, number][] = (() => {
  const dots: [number, number][] = [];
  for (let col = 0; col < 18; col++) {
    for (let row = 0; row < 9; row++) {
      const x = 9 + col * 6;
      const y = 8 + row * 6;
      const inside =
        ((x - 33) / 22) ** 2 + ((y - 27) / 18) ** 2 < 1 ||
        ((x - 82) / 26) ** 2 + ((y - 24) / 15) ** 2 < 1 ||
        ((x - 92) / 11) ** 2 + ((y - 50) / 7) ** 2 < 1;
      if (inside) dots.push([x, y]);
    }
  }
  return dots;
})();

function Mountain({ points, sun }: { points: string; sun?: [number, number] }) {
  return (
    <>
      <Poly points={points} tone="mid" />
      {sun && <Dot x={sun[0]} y={sun[1]} r={4} tone="accent" />}
    </>
  );
}

function Chip({ x, y, w, filled }: { x: number; y: number; w: number; filled: boolean }) {
  return filled ? (
    <Box x={x} y={y} w={w} h={9} r={4.5} tone="accent" />
  ) : (
    <Box x={x} y={y} w={w} h={9} r={4.5} tone="none" stroke="mid" />
  );
}

type LogoMark = (cx: number, cy: number) => ReactNode;

const LOGO_CELLS: readonly [number, number][] = [
  [8, 6],
  [44, 6],
  [80, 6],
  [8, 34],
  [44, 34],
  [80, 34],
];

const LOGO_MARKS: readonly LogoMark[] = [
  (cx, cy) => (
    <>
      <Dot x={cx - 7} y={cy} r={3.5} />
      <Box x={cx - 2} y={cy - 2} w={11} h={4} r={2} tone="mid" />
    </>
  ),
  (cx, cy) => <Box x={cx - 8} y={cy - 4} w={16} h={8} r={2} tone="accent" />,
  (cx, cy) => <Poly points={`${cx},${cy - 5} ${cx + 6},${cy + 5} ${cx - 6},${cy + 5}`} tone="mid" />,
  (cx, cy) => <Box x={cx - 9} y={cy - 2} w={18} h={4} r={2} tone="mid" />,
  (cx, cy) => <Dot x={cx} y={cy} r={5} />,
  (cx, cy) => (
    <>
      <Box x={cx - 9} y={cy - 3} w={6} h={6} r={1} tone="mid" />
      <Box x={cx - 1} y={cy - 2} w={10} h={4} r={2} tone="mid" />
    </>
  ),
];

const sankeyBand = (a0: number, a1: number, b0: number, b1: number) =>
  `M14 ${a0} C60 ${a0} 60 ${b0} 106 ${b0} L106 ${b1} C60 ${b1} 60 ${a1} 14 ${a1} Z`;

const ECOSYSTEM_SATELLITES = [200, 255, 320, 20, 130].map((deg) => polar(60, 32, 24, deg));

const GANTT_ROWS: readonly [number, number, Tone][] = [
  [32, 30, "soft"],
  [50, 28, "mid"],
  [68, 30, "accent"],
  [88, 24, "soft"],
];

const STREAM_LANES: readonly [number, readonly [number, number, Tone][]][] = [
  [14, [[12, 30, "mid"], [50, 24, "soft"], [80, 28, "soft"]]],
  [32, [[20, 22, "soft"], [48, 40, "accent"], [94, 16, "soft"]]],
  [50, [[8, 36, "soft"], [52, 20, "mid"], [78, 30, "soft"]]],
];

const KEYWORD_GROUPS: readonly [number, number, readonly [number, number][], number][] = [
  [6, 28, [[8, 24], [35, 30], [68, 20]], -1],
  [32, 22, [[8, 30], [41, 22], [66, 28]], 1],
];

const GLYPHS: Record<InsertableModuleType, () => ReactElement> = {
  bar: () => (
    <>
      <Stroke d="M8 58 H112" dash="2 3" />
      {[18, 28, 24, 36, 46, 32].map((h, i) => (
        <Box key={i} x={14 + i * 17} y={57 - h} w={11} h={h} tone={i === 4 ? "accent" : "mid"} />
      ))}
    </>
  ),
  line: () => (
    <>
      <Stroke d="M8 22 H112 M8 39 H112" tone="faint" dash="2 3" />
      <Stroke d="M8 56 H112" />
      <Stroke d="M8 54 L28 50 L46 52 L66 44 L86 46 L112 38" tone="mid" />
      <Stroke d="M8 48 L28 40 L46 44 L66 26 L86 30 L112 12" tone="accent" width={2.5} />
      <Dot x={112} y={12} r={3} tone="accent" />
    </>
  ),
  pie: () => (
    <>
      <Dot x={36} y={32} r={22} tone="soft" />
      <Arc x={36} y={32} r={11} start={0} length={36} width={22} tone="accent" />
      <Arc x={36} y={32} r={11} start={36} length={26} width={22} tone="mid" />
      {[18, 30, 42].map((y, i) => (
        <g key={y}>
          <Box x={70} y={y - 1} w={6} h={6} tone={i === 0 ? "accent" : i === 1 ? "mid" : "soft"} />
          <Text x={80} y={y} w={[30, 24, 28][i]} />
        </g>
      ))}
    </>
  ),
  scatter: () => (
    <>
      <Stroke d="M10 6 V56 H114" />
      {[
        [22, 46],
        [30, 40],
        [38, 47],
        [46, 34],
        [54, 39],
        [62, 28],
        [72, 31],
        [80, 22],
        [90, 25],
      ].map(([x, y]) => (
        <Dot key={`${x}-${y}`} x={x} y={y} r={2.5} />
      ))}
      <Dot x={102} y={14} r={4} tone="accent" />
    </>
  ),
  area: () => (
    <>
      <Stroke d="M8 56 H112" />
      <Poly points="8,56 8,42 28,34 46,38 66,22 86,26 112,10 112,56" tone="soft" />
      <Stroke d="M8 42 L28 34 L46 38 L66 22 L86 26 L112 10" tone="accent" width={2.5} />
    </>
  ),
  image: () => (
    <>
      <Box x={16} y={6} w={88} h={52} r={4} tone="soft" />
      <Mountain points="20,54 44,28 62,44 74,34 100,54" sun={[86, 20]} />
    </>
  ),
  progress: () => (
    <>
      {[6, 24, 42].map((y, i) => (
        <g key={y}>
          <Text x={10} y={y} w={[30, 24, 34][i]} />
          <Box x={10} y={y + 7} w={100} h={5} r={2.5} tone="soft" />
          <Box x={10} y={y + 7} w={[78, 56, 40][i]} h={5} r={2.5} tone={i === 0 ? "accent" : "mid"} />
        </g>
      ))}
    </>
  ),
  quote: () => (
    <>
      {[10, 24].map((x) => (
        <g key={x}>
          <Dot x={x + 5} y={22} r={5} tone="accent" />
          <Stroke d={`M${x} 22 C${x} 13 ${x + 4} 9 ${x + 9} 8`} tone="accent" width={3} />
        </g>
      ))}
      <Text x={42} y={12} w={68} />
      <Text x={42} y={21} w={62} />
      <Text x={42} y={30} w={48} />
      <Dot x={46} y={48} r={4} tone="mid" />
      <Text x={54} y={46} w={30} tone="mid" />
    </>
  ),
  "feature-comparison": () => (
    <>
      <Box x={8} y={8} w={104} h={8} tone="mid" />
      {[20, 32, 44].map((y, row) =>
        [
          [8, 34],
          [46, 30],
          [80, 32],
        ].map(([x, w], col) => (
          <Box key={`${row}-${col}`} x={x} y={y} w={w} h={8} tone={row === 1 && col === 1 ? "accent" : "soft"} />
        )),
      )}
    </>
  ),
  table: () => (
    <>
      <Box x={8} y={6} w={104} h={8} r={2} tone="mid" />
      {[18, 29, 40, 51].map((y, i) => (
        <g key={y}>
          <Text x={10} y={y} w={[34, 28, 38, 30][i]} />
          <Dot x={56} y={y + 2} r={2} tone={i === 1 ? "accent" : "mid"} />
          <Text x={61} y={y} w={[18, 22, 16, 20][i]} tone="faint" />
          <Text x={110 - [20, 26, 16, 22][i]} y={y} w={[20, 26, 16, 22][i]} tone="mid" />
          {i < 3 && <Stroke d={`M8 ${y + 7.5} H112`} tone="faint" width={1} dash="2 2" />}
        </g>
      ))}
    </>
  ),
  "ring-kpis": () => (
    <>
      {[6, 44, 82].map((x, i) => (
        <g key={x}>
          <Box x={x} y={6} w={32} h={52} r={4} tone="paper" stroke="soft" />
          <Text x={x + 5} y={12} w={[18, 14, 20][i]} />
          <Arc x={x + 12} y={31} r={6} start={0} length={100} width={2.5} tone="soft" />
          <Arc x={x + 12} y={31} r={6} start={0} length={[72, 48, 86][i]} width={2.5} tone={i === 0 ? "accent" : "mid"} />
          <Box x={x + 5} y={44} w={[20, 16, 18][i]} h={6} r={1.5} tone="mid" />
        </g>
      ))}
    </>
  ),
  "metric-tabs-chart": () => (
    <>
      <Box x={8} y={4} w={104} h={18} r={3} tone="paper" stroke="soft" />
      <Stroke d="M42.7 4 V22 M77.3 4 V22" tone="soft" width={1} />
      {[8, 42.7, 77.3].map((x, i) => (
        <g key={x}>
          <Text x={x + 4} y={7} w={16} tone="faint" />
          <Box x={x + 4} y={13} w={22} h={5} r={1.5} tone={i === 0 ? "accent" : "mid"} />
        </g>
      ))}
      <Poly points="8,60 8,50 30,44 52,47 74,36 96,38 112,28 112,60" tone="soft" />
      <Stroke d="M8 54 L30 50 L52 52 L74 44 L96 46 L112 40" tone="mid" dash="3 3" />
      <Stroke d="M8 50 L30 44 L52 47 L74 36 L96 38 L112 28" tone="accent" width={2.5} />
    </>
  ),
  breakdown: () => (
    <>
      <Text x={8} y={4} w={30} tone="faint" />
      <Box x={8} y={11} w={42} h={10} r={2} tone="mid" />
      {[28, 40, 52].map((y, i) => (
        <g key={y}>
          <Text x={8} y={y - 3} w={[32, 26, 22][i]} />
          <Text x={96} y={y - 3} w={16} tone="faint" />
          <Box x={8} y={y + 3} w={104} h={4} r={2} tone="faint" />
          <Box x={8} y={y + 3} w={[82, 54, 28][i]} h={4} r={2} tone={i === 0 ? "accent" : "mid"} />
        </g>
      ))}
    </>
  ),
  "dual-metric": () => (
    <>
      {[4, 34].map((y, row) => (
        <g key={y}>
          <Box x={8} y={y} w={[34, 26][row]} h={10} r={2} tone={row === 0 ? "accent" : "mid"} />
          <Box x={[46, 38][row]} y={y + 2} w={14} h={6} r={3} tone="soft" />
          <Text x={8} y={y + 13} w={28} tone="faint" />
          {Array.from({ length: 20 }, (_, t) => (
            <Box
              key={t}
              x={8 + t * 5.2}
              y={y + 20}
              w={3.4}
              h={6}
              r={1}
              tone={t < [8, 12][row] ? (row === 0 ? "accent" : "mid") : "faint"}
            />
          ))}
        </g>
      ))}
    </>
  ),
  "stage-bars": () => (
    <>
      {[4, 17, 30, 43].map((y, i) => (
        <g key={y}>
          <Text x={8} y={y + 2} w={[20, 16, 22, 14][i]} />
          <Box x={34} y={y} w={[78, 52, 30, 14][i]} h={8} r={2} tone={(["accent", "strong", "mid", "soft"] as const)[i]} />
        </g>
      ))}
      <Stroke d="M8 57 H112" tone="faint" width={1} dash="2 2" />
      {[8, 38, 68].map((x) => (
        <Text key={x} x={x} y={59} w={24} tone="faint" />
      ))}
    </>
  ),
  "insight-panel": () => (
    <>
      <Box x={8} y={4} w={104} h={56} r={5} tone="accent" />
      <Box x={14} y={10} w={26} h={7} r={3.5} tone="paper" />
      <Box x={14} y={27} w={30} h={12} r={2} tone="paper" />
      <Text x={14} y={43} w={78} tone="paper" />
      <Text x={14} y={49} w={56} tone="paper" />
      {[14, 32, 50].map((x, i) => (
        <Box key={x} x={x} y={55} w={15} h={1.5} r={0.75} tone={i === 0 ? "paper" : "accentSoft"} />
      ))}
    </>
  ),
  pyramid: () => (
    <>
      <Poly points="60,6 69.8,18 50.2,18" tone="accent" />
      <Poly points="48.6,20 71.4,20 81.2,32 38.8,32" tone="mid" />
      <Poly points="37.2,34 82.8,34 92.6,46 27.4,46" tone="soft" />
      <Poly points="25.8,48 94.2,48 104,60 16,60" tone="mid" />
    </>
  ),
  "dotted-map": () => (
    <>
      {MAP_DOTS.map(([x, y]) => (
        <Dot key={`${x}-${y}`} x={x} y={y} r={1.6} tone="mid" />
      ))}
      <Shape d="M78 34 C73 27 71 24 71 20.5 A7 7 0 1 1 85 20.5 C85 24 83 27 78 34 Z" tone="accent" />
      <Dot x={78} y={20.5} r={2.5} tone="paper" />
    </>
  ),
  "radial-bar": () => (
    <>
      {[26, 19, 12].map((r, i) => (
        <g key={r}>
          <Arc x={60} y={32} r={r} start={0} length={100} width={5} tone="soft" />
          <Arc x={60} y={32} r={r} start={0} length={[72, 55, 38][i]} width={5} tone={i === 0 ? "accent" : "mid"} round />
        </g>
      ))}
    </>
  ),
  candlestick: () => (
    <>
      <Stroke d="M8 58 H112" dash="2 3" />
      {[
        [30, 42, 24, 48],
        [26, 36, 20, 40],
        [32, 46, 28, 52],
        [22, 34, 16, 38],
        [18, 28, 12, 32],
        [24, 38, 20, 44],
        [14, 26, 8, 30],
      ].map(([top, bottom, high, low], i) => {
        const x = 14 + i * 15;
        const tone: Tone = i === 4 ? "accent" : "mid";
        return (
          <g key={x}>
            <Stroke d={`M${x + 4} ${high} V${low}`} tone={tone} />
            <Box x={x} y={top} w={8} h={bottom - top} r={1} tone={tone} />
          </g>
        );
      })}
    </>
  ),
  funnel: () => (
    <>
      {[100, 76, 52, 28].map((w, i) => (
        <Box
          key={w}
          x={60 - w / 2}
          y={6 + i * 14}
          w={w}
          h={10}
          r={2}
          tone={i === 3 ? "accent" : i === 2 ? "mid" : "soft"}
        />
      ))}
    </>
  ),
  heatmap: () => (
    <>
      {Array.from({ length: 5 }).map((_, row) =>
        Array.from({ length: 10 }).map((__, col) => (
          <Box
            key={`${row}-${col}`}
            x={6 + col * 11}
            y={5 + row * 11}
            w={9}
            h={9}
            r={1.5}
            tone={row === 2 && col === 6 ? "accent" : HEAT_TONES[(col * 7 + row * 3 + col * row) % 4]}
          />
        )),
      )}
    </>
  ),
  "profit-loss": () => (
    <>
      <Stroke d="M8 32 H112" dash="2 3" />
      {[16, 22, -10, 12, -18, 26, -8].map((value, i) => (
        <Box
          key={i}
          x={12 + i * 14}
          y={value > 0 ? 32 - value : 32}
          w={10}
          h={Math.abs(value)}
          r={1}
          tone={i === 5 ? "accent" : value > 0 ? "mid" : "soft"}
        />
      ))}
    </>
  ),
  radar: () => (
    <>
      <polygon
        points={polarPoints(60, 32, [26, 26, 26, 26, 26, 26])}
        className={cn("fill-none", STROKE.soft)}
        strokeWidth={1.25}
      />
      <polygon
        points={polarPoints(60, 32, [14, 14, 14, 14, 14, 14])}
        className={cn("fill-none", STROKE.soft)}
        strokeWidth={1.25}
      />
      <polygon
        points={polarPoints(60, 32, [22, 16, 24, 12, 20, 14])}
        className={cn(FILL.accentSoft, STROKE.accent)}
        strokeWidth={2}
        strokeLinejoin="round"
      />
    </>
  ),
  sankey: () => (
    <>
      <Shape d={sankeyBand(8, 22, 6, 20)} tone="soft" />
      <Shape d={sankeyBand(22, 30, 24, 32)} tone="soft" />
      <Shape d={sankeyBand(34, 42, 32, 40)} tone="soft" />
      <Shape d={sankeyBand(42, 56, 44, 58)} tone="accentSoft" />
      <Box x={8} y={8} w={6} h={22} r={1} tone="mid" />
      <Box x={8} y={34} w={6} h={22} r={1} tone="mid" />
      <Box x={106} y={6} w={6} h={14} r={1} tone="mid" />
      <Box x={106} y={24} w={6} h={16} r={1} tone="mid" />
      <Box x={106} y={44} w={6} h={14} r={1} tone="accent" />
    </>
  ),
  sunburst: () => (
    <>
      <Dot x={60} y={32} r={9} tone="mid" />
      {[
        [0, 38],
        [39, 30],
        [70, 29],
      ].map(([start, length]) => (
        <Arc key={start} x={60} y={32} r={15} start={start} length={length} width={8} tone="mid" />
      ))}
      {[
        [0, 18],
        [19, 19],
        [39, 14],
        [54, 15],
        [70, 13],
        [84, 15],
      ].map(([start, length], i) => (
        <Arc key={start} x={60} y={32} r={24} start={start} length={length} width={7} tone={i === 0 ? "accent" : "soft"} />
      ))}
    </>
  ),
  "progress-stages": () => (
    <>
      <Stroke d="M16 24 H104" width={2} />
      <Stroke d="M16 24 H75" tone="mid" width={2} />
      {[16, 45, 75, 104].map((x, i) => (
        <g key={x}>
          {i === 3 ? (
            <Dot x={x} y={24} r={6} tone="paper" stroke="mid" />
          ) : (
            <Dot x={x} y={24} r={6} tone={i === 2 ? "accent" : "mid"} />
          )}
          <Text x={x - 9} y={36} w={18} />
          <Text x={x - 6} y={44} w={12} tone="faint" />
        </g>
      ))}
    </>
  ),
  "fork-join": () => (
    <>
      <Stroke d="M17 32 C38 32 34 12 48 12 M72 12 C86 12 82 32 103 32" width={2} />
      <Stroke d="M17 32 C38 32 34 52 48 52 M72 52 C86 52 82 32 103 32" width={2} />
      <Stroke d="M17 32 H103" tone="accent" width={2} />
      <Box x={48} y={8} w={24} h={8} r={2} tone="soft" />
      <Box x={48} y={28} w={24} h={8} r={2} tone="accent" />
      <Box x={48} y={48} w={24} h={8} r={2} tone="soft" />
      <Dot x={12} y={32} r={5} tone="mid" />
      <Dot x={108} y={32} r={5} tone="mid" />
    </>
  ),
  "gantt-phases": () => (
    <>
      <Stroke d="M50 4 V58 M70 4 V58 M90 4 V58" tone="faint" dash="2 3" />
      {GANTT_ROWS.map(([x, w, tone], i) => (
        <g key={i}>
          <Text x={8} y={10 + i * 13} w={16} />
          <Box x={x} y={8 + i * 13} w={w} h={8} r={2} tone={tone} />
        </g>
      ))}
    </>
  ),
  "parallel-streams": () => (
    <>
      {STREAM_LANES.map(([y, segments]) => (
        <g key={y}>
          <Stroke d={`M8 ${y} H112`} tone="faint" />
          {segments.map(([x, w, tone]) => (
            <Box key={x} x={x} y={y - 4} w={w} h={8} r={4} tone={tone} />
          ))}
        </g>
      ))}
    </>
  ),
  "segmented-progress": () => (
    <>
      <Text x={8} y={8} w={36} tone="mid" />
      <Box x={8} y={22} w={40} h={10} r={2} tone="accent" />
      <Box x={50} y={22} w={26} h={10} r={2} tone="mid" />
      <Box x={78} y={22} w={16} h={10} r={2} tone="soft" />
      <Box x={96} y={22} w={16} h={10} r={2} tone="faint" />
      {[8, 44, 80].map((x, i) => (
        <g key={x}>
          <Box x={x} y={44} w={5} h={5} r={1} tone={i === 0 ? "accent" : i === 1 ? "mid" : "soft"} />
          <Text x={x + 8} y={44.5} w={18} />
        </g>
      ))}
    </>
  ),
  "state-machine": () => (
    <>
      <Stroke d="M40 15 H79 M75 11.5 L79 15 L75 18.5" tone="mid" />
      <Stroke d="M96 24 L72 42 M77.8 41.4 L72 42 L74.2 36.6" tone="mid" />
      <Stroke d="M48 42 L24 24 M29.8 24.6 L24 24 L26.2 29.4" tone="mid" />
      <Box x={8} y={8} w={30} h={14} r={4} tone="accent" />
      <Box x={82} y={8} w={30} h={14} r={4} tone="paper" stroke="mid" />
      <Box x={45} y={44} w={30} h={14} r={4} tone="paper" stroke="mid" />
    </>
  ),
  "timeline-stepper": () => (
    <>
      <Stroke d="M14 8 V56" dash="2 3" />
      {[12, 32, 52].map((y, i) => (
        <g key={y}>
          {i === 2 ? (
            <Dot x={14} y={y} r={4} tone="paper" stroke="mid" />
          ) : (
            <Dot x={14} y={y} r={4} tone={i === 0 ? "accent" : "mid"} />
          )}
          <Text x={26} y={y - 5} w={[44, 36, 40][i]} tone="mid" />
          <Text x={26} y={y + 2} w={[70, 62, 56][i]} />
        </g>
      ))}
    </>
  ),
  "quadrant-matrix": () => (
    <>
      <Box x={14} y={4} w={47} h={27} r={2} tone="faint" />
      <Box x={63} y={4} w={47} h={27} r={2} tone="faint" />
      <Box x={14} y={33} w={47} h={27} r={2} tone="faint" />
      <Box x={63} y={33} w={47} h={27} r={2} tone="faint" />
      {[
        [30, 16],
        [44, 22],
        [80, 46],
        [94, 40],
        [28, 48],
        [48, 44],
      ].map(([x, y]) => (
        <Dot key={`${x}-${y}`} x={x} y={y} r={3} />
      ))}
      <Dot x={90} y={16} r={4} tone="accent" />
    </>
  ),
  "correlation-heatmap": () => (
    <>
      {Array.from({ length: 6 }).map((_, row) =>
        Array.from({ length: row + 1 }).map((__, col) => (
          <Box
            key={`${row}-${col}`}
            x={32 + col * 9.5}
            y={4 + row * 9.5}
            w={8}
            h={8}
            r={1.5}
            tone={
              row === 4 && col === 1
                ? "accent"
                : row === col
                  ? "strong"
                  : HEAT_TONES[(row * 5 + col * 3) % 3]
            }
          />
        )),
      )}
    </>
  ),
  "skill-level-matrix": () => (
    <>
      {[4, 2, 5, 3].map((level, row) => {
        const y = 10 + row * 14;
        return (
          <g key={row}>
            <Text x={8} y={y - 2} w={[28, 22, 30, 24][row]} />
            {Array.from({ length: 5 }).map((_, i) =>
              i < level ? (
                <Dot key={i} x={52 + i * 13} y={y} r={4} tone={row === 2 ? "accent" : "mid"} />
              ) : (
                <Dot key={i} x={52 + i * 13} y={y} r={3.5} tone="paper" stroke="soft" />
              ),
            )}
          </g>
        );
      })}
    </>
  ),
  "node-cycle": () => (
    <>
      <Dot x={60} y={32} r={22} tone="none" stroke="soft" />
      {[0, 1, 2, 3, 4].map((i) => {
        const [x, y] = polar(60, 32, 22, -90 + i * 72);
        return <Dot key={i} x={x} y={y} r={6} tone={i === 0 ? "accent" : "mid"} />;
      })}
    </>
  ),
  "segmented-cycle": () => (
    <>
      <Arc x={60} y={32} r={21} start={1.5} length={22} width={9} tone="accent" />
      <Arc x={60} y={32} r={21} start={26.5} length={22} width={9} tone="mid" />
      <Arc x={60} y={32} r={21} start={51.5} length={22} width={9} tone="soft" />
      <Arc x={60} y={32} r={21} start={76.5} length={22} width={9} tone="mid" />
    </>
  ),
  "ecosystem-map": () => (
    <>
      <Dot x={60} y={32} r={24} tone="none" stroke="soft" />
      {ECOSYSTEM_SATELLITES.map(([x, y], i) => (
        <Stroke key={i} d={`M60 32 L${x.toFixed(1)} ${y.toFixed(1)}`} tone="faint" />
      ))}
      {ECOSYSTEM_SATELLITES.map(([x, y], i) => (
        <Dot key={i} x={x} y={y} r={i % 2 === 0 ? 5 : 4} tone="mid" />
      ))}
      <Dot x={60} y={32} r={9} tone="accent" />
    </>
  ),
  "hierarchy-tree": () => (
    <>
      <Stroke d="M60 18 V40 M23 40 V29 H97 V40" tone="mid" />
      <Box x={46} y={6} w={28} h={12} r={3} tone="accent" />
      {[10, 47, 84].map((x) => (
        <Box key={x} x={x} y={40} w={26} h={12} r={3} tone="soft" />
      ))}
    </>
  ),
  "parallel-category-trees": () => (
    <>
      {[10, 64].map((x, col) => (
        <g key={x}>
          <Box x={x} y={6} w={40} h={8} r={2} tone={col === 0 ? "accent" : "mid"} />
          <Stroke d={`M${x + 4} 14 V47`} />
          {[20, 32, 44].map((y, i) => (
            <g key={y}>
              <Stroke d={`M${x + 4} ${y + 3} H${x + 12}`} />
              <Box x={x + 14} y={y} w={[26, 22, 30][(i + col) % 3]} h={6} r={2} tone="soft" />
            </g>
          ))}
        </g>
      ))}
    </>
  ),
  "activity-feed": () => (
    <>
      <Stroke d="M8 22 H112 M8 41 H112" tone="faint" dash="2 3" />
      {[6, 25, 44].map((y, i) => (
        <g key={y}>
          <Dot x={14} y={y + 7} r={6} tone={i === 0 ? "accent" : "mid"} />
          <Text x={26} y={y + 2} w={[50, 42, 46][i]} tone="mid" />
          <Text x={26} y={y + 9} w={[70, 62, 54][i]} />
          <Text x={100} y={y + 2} w={12} tone="faint" />
        </g>
      ))}
    </>
  ),
  "metric-list": () => (
    <>
      <Stroke d="M8 17 H112 M8 31 H112 M8 45 H112" tone="faint" dash="2 3" />
      {[30, 42, 54, 36].map((w, i) => {
        const y = 10 + i * 14;
        return (
          <g key={i}>
            <Text x={8} y={y - 2} w={[36, 28, 40, 32][i]} />
            <Box x={112 - w} y={y - 3} w={w} h={6} r={3} tone={i === 2 ? "accent" : "mid"} />
          </g>
        );
      })}
    </>
  ),
  "delta-comparison": () => (
    <>
      <Text x={8} y={14} w={24} />
      <Box x={8} y={24} w={34} h={12} r={2} tone="mid" />
      <Text x={8} y={42} w={28} />
      <Stroke d="M50 30 H62 M58.5 26.5 L62 30 L58.5 33.5" tone="mid" />
      <Text x={70} y={14} w={24} />
      <Box x={70} y={24} w={42} h={12} r={2} tone="accent" />
      <Text x={70} y={42} w={20} />
    </>
  ),
  "split-comparison": () => (
    <>
      <Stroke d="M60 6 V58" />
      {[8, 68].map((x, col) => (
        <g key={x}>
          <Box x={x} y={8} w={30} h={6} r={2} tone={col === 1 ? "accent" : "mid"} />
          {[20, 28, 36, 44].map((y, i) => (
            <Text key={y} x={x} y={y} w={[44, 38, 44, 30][(i + col) % 4]} />
          ))}
        </g>
      ))}
    </>
  ),
  "social-proof-stats": () => (
    <>
      {[14, 24, 34, 44].map((x) => (
        <Dot key={x} x={x} y={14} r={7} tone="mid" stroke="paper" />
      ))}
      <Text x={56} y={12} w={40} />
      {[8, 46, 84].map((x, i) => (
        <g key={x}>
          <Box x={x} y={32} w={26} h={10} r={2} tone={i === 0 ? "accent" : "mid"} />
          <Text x={x} y={47} w={22} />
        </g>
      ))}
    </>
  ),
  "list-cards": () => (
    <>
      {[4, 22, 40].map((y, i) => (
        <g key={y}>
          <Box x={8} y={y} w={104} h={16} r={3} tone="paper" stroke="soft" />
          <Box x={14} y={y + 4} w={8} h={8} r={2} tone={i === 0 ? "accent" : "mid"} />
          <Text x={28} y={y + 3.5} w={[44, 38, 48][i]} tone="mid" />
          <Text x={28} y={y + 9} w={[66, 58, 62][i]} />
        </g>
      ))}
    </>
  ),
  "big-number-chart": () => (
    <>
      <text x={8} y={40} fontSize={30} className="fill-primary font-medium tabular-nums">
        42
      </text>
      <Text x={10} y={48} w={40} />
      <Stroke d="M68 46 L78 40 L86 42 L96 30 L112 22" tone="mid" width={2} />
    </>
  ),
  "pricing-tiers": () => (
    <>
      {[
        [8, 10, 46],
        [44, 4, 56],
        [80, 10, 46],
      ].map(([x, y, h], i) => {
        const featured = i === 1;
        return (
          <g key={x}>
            <Box x={x} y={y} w={32} h={h} r={3} tone="paper" stroke={featured ? "accent" : "soft"} />
            <Box x={x + 5} y={y + 6} w={14} h={3.5} r={1.75} tone="soft" />
            <Box x={x + 5} y={y + 13} w={18} h={7} r={1.5} tone="mid" />
            {[24, 30, 36].map((offset) => (
              <Box key={offset} x={x + 5} y={y + offset} w={22} h={3} r={1.5} tone="soft" />
            ))}
            <Box x={x + 5} y={y + h - 9} w={22} h={5} r={2} tone={featured ? "accent" : "soft"} />
          </g>
        );
      })}
    </>
  ),
  "roi-calculator": () => (
    <>
      <Text x={8} y={8} w={24} />
      <Box x={8} y={18} w={50} h={3} r={1.5} tone="soft" />
      <Box x={8} y={18} w={32} h={3} r={1.5} tone="accent" />
      <Dot x={40} y={19.5} r={4} tone="paper" stroke="accent" />
      <Text x={8} y={32} w={20} />
      <Box x={8} y={42} w={50} h={3} r={1.5} tone="soft" />
      <Box x={8} y={42} w={20} h={3} r={1.5} tone="mid" />
      <Dot x={28} y={43.5} r={4} tone="paper" stroke="mid" />
      <Box x={66} y={8} w={46} h={48} r={4} tone="faint" />
      <Text x={72} y={16} w={20} />
      <Box x={72} y={26} w={30} h={10} r={2} tone="mid" />
      <Text x={72} y={42} w={24} />
    </>
  ),
  "logo-wall": () => (
    <>
      {LOGO_CELLS.map(([x, y], i) => (
        <g key={`${x}-${y}`}>
          <Box x={x} y={y} w={32} h={24} r={3} tone="none" stroke="soft" dash="3 2" />
          {LOGO_MARKS[i](x + 16, y + 12)}
        </g>
      ))}
    </>
  ),
  "before-after-annotated": () => (
    <>
      <Box x={8} y={8} w={48} h={40} r={3} tone="soft" />
      <Box x={64} y={8} w={48} h={40} r={3} tone="soft" />
      <Mountain points="11,45 26,26 38,38 44,32 53,45" />
      <Mountain points="67,45 82,26 94,38 100,32 109,45" />
      <Dot x={82} y={20} r={4} tone="accent" stroke="paper" />
      <Text x={8} y={54} w={20} />
      <Text x={64} y={54} w={24} />
    </>
  ),
  "before-after-slider": () => (
    <>
      <Box x={8} y={6} w={104} h={52} r={4} tone="soft" />
      <Box x={8} y={6} w={52} h={52} r={4} tone="mid" />
      <Box x={44} y={6} w={16} h={52} r={0} tone="mid" />
      <Poly points="12,54 38,28 58,46 72,36 108,54" tone="soft" />
      <Stroke d="M60 6 V58" tone="accent" width={2} />
      <Dot x={60} y={32} r={7} tone="accent" />
      <Stroke d="M58 29 L55 32 L58 35 M62 29 L65 32 L62 35" tone="paper" width={1.5} />
    </>
  ),
  "keywords-grouped": () => (
    <>
      {KEYWORD_GROUPS.map(([y, headerWidth, chips, accentIndex]) => (
        <g key={y}>
          <Text x={8} y={y} w={headerWidth} tone="mid" />
          {chips.map(([x, w], i) => (
            <Chip key={x} x={x} y={y + 9} w={w} filled={i === accentIndex} />
          ))}
        </g>
      ))}
    </>
  ),
  "keywords-explained": () => (
    <>
      {[6, 25, 44].map((y, i) => (
        <g key={y}>
          <Chip x={8} y={y} w={26} filled={i === 0} />
          <Text x={40} y={y} w={[72, 64, 68][i]} />
          <Text x={40} y={y + 6.5} w={[56, 48, 52][i]} tone="faint" />
        </g>
      ))}
    </>
  ),
};

const EXCLUDED_TYPES: ReadonlySet<string> = new Set(HANDYOUT_SECTION_KEYS);

function isInsertable(type: AllModuleTypeNames): type is InsertableModuleType {
  return !EXCLUDED_TYPES.has(type);
}

export function ModuleGlyph({ type, className }: { type: AllModuleTypeNames; className?: string }) {
  if (!isInsertable(type)) {
    const Icon = MODULE_TYPE_META[type].icon;
    return (
      <div aria-hidden className={cn("flex items-center justify-center text-muted-foreground", className)}>
        <Icon className="size-5" strokeWidth={1.5} />
      </div>
    );
  }
  const Glyph = GLYPHS[type];
  return (
    <svg aria-hidden viewBox="0 0 120 64" className={cn("block", className)}>
      <Glyph />
    </svg>
  );
}
